import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"
import Sortable from "sortablejs"
import { ResizableTableColumns } from "@validide/resizable-table-columns"

import { readCookie, setCookie } from "modules/cookie_helpers"
import localStore from "../modules/local_store"

export default class extends Controller {
  static targets = [
    "table",
    "selectedCount",
    "totalCount",
    "applyToAllMatching",
    "selectAllMatchingAlert",
    "selectAllCurrentPageAlert",
    "exportBtn"
  ];

  static outlets = [
    'checkbox-select-all',
    'toolbar'
  ];

  connect() {
    this.savedViewId = this.queryParams.get('saved_view_id');
    this.tableName = this.element.closest('turbo-frame').id;
    this.tableContainer = this.element.querySelector('.table-container');

    this.#restoreFilterPreference();
    this.#initResizableColumns();
    this.#initReorderableColumns();
  }

  ///////////////
  /// Actions ///
  ///////////////

  toggleFilters() {
    this.showFilter = !this.showFilter;
    this.#applyFilterToggle();
    this.#saveFilterPreference();
    this.rtcObject?.handleWindowResize();
  }

  selectAllMatchingRecords() {
    this.#setApplyToAll(true);
    if (this.hasSelectAllCurrentPageAlertTarget) this.#hideAlert(this.selectAllCurrentPageAlertTarget);
    if (this.hasSelectAllMatchingAlertTarget)    this.#showAndScrollTo(this.selectAllMatchingAlertTarget);
    this.#updateSelectedCount();
  }

  selectAllCurrentPageRecords() {
    this.#setApplyToAll(false);
    if (this.hasSelectAllMatchingAlertTarget)    this.#hideAlert(this.selectAllMatchingAlertTarget);
    if (this.hasSelectAllCurrentPageAlertTarget) this.#showAndScrollTo(this.selectAllCurrentPageAlertTarget);
    this.#updateSelectedCount();
  }

  async requestBatchActionModal(event) {
    const batchAction = event.currentTarget.dataset.batchAction;
    if (!batchAction) return;

    const queryParams = { apply_to_all_filter_matches: this.applyToAll };
    if (!this.applyToAll) queryParams['record_ids'] = this.selectedRecords;

    const request = new FetchRequest('get', `/${this.tableName}/batch_actions/${batchAction}${window.location.search}`, {
      query: queryParams,
      responseKind: 'turbo-stream'
    });

    const response = await request.perform();
    if (response.ok) {
      setTimeout(() => $(`#${batchAction}`).modal('show'), 20);
    }
  }

  export(event) {
    Turbo.visit(this.#buildExportUrl(event.currentTarget.dataset.format));
  }

  /////////////////
  /// Callbacks ///
  /////////////////

  checkboxSelectAllOutletConnected(outlet, element) {
    element.addEventListener("change", this.#handleCheckboxChange.bind(this));
    if (outlet.hasCheckboxAllTarget) {
      outlet.checkboxAllTarget.addEventListener("change", this.#toggleSelectionAlert.bind(this));
    }
  }

  checkboxSelectAllOutletDisconnected(outlet, element) {
    element.removeEventListener("change", this.#handleCheckboxChange.bind(this));
    if (outlet.hasCheckboxAllTarget) {
      outlet.checkboxAllTarget.removeEventListener("change", this.#toggleSelectionAlert.bind(this));
    }
  }

  ///////////////
  /// Helpers ///
  ///////////////

  #handleCheckboxChange(event) {
    if (!event.target.matches("input[type='checkbox']")) return;
    this.#updateToolbarVisibility();
    this.#updateSelectedCount();
    this.#highlightSelectedRows();
  }

  #updateToolbarVisibility() {
    if (this.hasToolbarOutlet) {
      this.toolbarOutlet.element.classList.toggle('hidden', this.checkedSize <= 0);
    }
  }

  #updateSelectedCount() {
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = this.selectedRecordsSize;
    }
    this.#updateExportButtonTitles();
  }

  #highlightSelectedRows() {
    this.checkboxSelectAllOutlet.checkboxTargets.forEach(cb => {
      cb.closest('tr').classList.toggle('info', cb.checked);
    });
  }

  #toggleSelectionAlert(event) {
    event.target.checked ? this.selectAllCurrentPageRecords() : this.#hideAllAlerts();
  }

  #hideAllAlerts() {
    if (this.hasSelectAllCurrentPageAlertTarget) this.#hideAlert(this.selectAllCurrentPageAlertTarget);
    if (this.hasSelectAllMatchingAlertTarget)    this.#hideAlert(this.selectAllMatchingAlertTarget);
  }

  #showAndScrollTo(element) {
    if (!element) return;
    element.classList.remove('hidden');
    this.tableContainer?.scrollTo({
      top: element.scrollTop,
      left: element.scrollLeft,
      behavior: "smooth"
    });
  }

  #hideAlert(element) {
    element?.classList.add('hidden');
  }

  #updateExportButtonTitles() {
    this.exportBtnTargets.forEach(btn => {
      const base = btn.textContent.match(/^[^(]+/)[0].trim();
      btn.textContent = this.selectedRecordsSize > 0 ? `${base} (${this.selectedRecordsSize} entries)` : base;
    });
  }

  #buildExportUrl(format) {
    const params = this.queryParams;
    if (this.selectedRecords.length > 0 && !this.applyToAll) {
      params.set("filter[id]", this.selectedRecords.join(","));
    }

    return `${window.location.pathname}.${format}?${params.toString()}`;
  }

  #initResizableColumns() {
    if (this.hasTableTarget && this.tableTarget.dataset.rtcResizableTable) {
      this.rtcObject = new ResizableTableColumns(this.tableTarget, {
        minWidth: 100,
        resizeFromBody: true,
        store: localStore
      });
    }
  }

  #initReorderableColumns() {
    if (!this.hasTableTarget || this.tableTarget.dataset.reorderable !== 'true') return;

    const headersRow = this.tableTarget.querySelector('thead tr');

    Sortable.create(headersRow, {
      animation: 150,
      filter: 'th.no-sort',
      preventOnFilter: false,
      handle: '.draggable',
      onEnd: (evt) => {
        let fromIndex = evt.oldIndex;
        let toIndex = evt.newIndex;

        // Don't allow moving to first (checkbox) column
        if (toIndex === 0 && this.hasCheckboxSelectAllOutlet) {
          const parent = evt.from;
          const item = evt.item;

          // If moved to first column (checkbox), change the target to 2nd column
          parent.insertBefore(item, parent.children[2]);
          toIndex = 1;
        }

        for (const row of this.tableTarget.tBodies[0].rows) {
          const cells = Array.from(row.cells);
          const movedCell = cells[fromIndex];
          if (fromIndex < toIndex) {
            row.cells[toIndex].after(movedCell);
          } else {
            row.cells[toIndex].before(movedCell);
          }
        }

        this.rtcObject?.syncHandleWidths();
        this.#saveCurrentViewToCookie();
      }
    });
  }

  #saveCurrentViewToCookie() {
    setCookie(this.cookieKey, this.selectedColumns);
  }

  #applyFilterToggle() {
    const tablePanel  = this.element.querySelector('.data-table-container');
    const filterPanel = this.element.querySelector('.filter-container');

    if (!tablePanel || !filterPanel) return;

    filterPanel.classList.toggle('hidden', !this.showFilter);
    tablePanel.classList.toggle('col-md-9', this.showFilter);
    tablePanel.classList.toggle('col-md-pull-3', this.showFilter);

    this.element.querySelector('#toggle-filter')?.classList.toggle('icon-active', this.showFilter);
  }

  #saveFilterPreference() {
    localStorage.setItem('data_table.show_filter', this.showFilter);
  }

  #restoreFilterPreference() {
    this.showFilter = localStorage.getItem('data_table.show_filter') === 'true';
    this.#applyFilterToggle();
  }

  #setApplyToAll(value) {
    if (this.hasApplyToAllMatchingTarget) {
      this.applyToAllMatchingTarget.value = value;
    }
  }

  ///////////////
  /// Getters ///
  ///////////////

  get queryParams() {
    return new URLSearchParams(window.location.search);
  }

  get checkedSize() {
    return this.checkboxSelectAllOutlet.checked.length;
  }

  get applyToAll() {
    return this.hasApplyToAllMatchingTarget
      && this.applyToAllMatchingTarget.value === 'true';
  }

  get selectedRecordsSize() {
    return this.applyToAll
      ? (this.hasTotalCountTarget && this.totalCountTarget.textContent) || 0
      : this.checkedSize;
  }

  get selectedRecords() {
    return this.checkboxSelectAllOutlet.checked.map(cb => cb.value);
  }

  get selectedColumns() {
    return Array.from(this.tableTarget.querySelectorAll('thead tr th'))
      .map(th => th.dataset.rtcResizable)
      .filter(Boolean)
      .join(',');
  }

  get cookieKey() {
    let key = `${this.tableName}-columns`;
    if (this.savedViewId) key += `-${this.savedViewId}`;
    return key;
  }
}
